import { zodResolver } from "@hookform/resolvers/zod";
import { LoadingButton } from "@mui/lab";
import { InputAdornment, Stack, Typography } from "@mui/material";
import { ErrorAlert } from "component/ErrorAlert";
import { FormDateTimePicker } from "component/FormDateTimePicker";
import { FormRadioGroup } from "component/FormRadioGroup";
import * as api from "api";
import { DEFAULT_TIME_ZONE, PLANNED_DEPARTURE_OPTIONS, RISK_SCORE_SELECT_ITEMS } from "constant";
import { format } from "date-fns-tz";
import { enAU } from "date-fns/locale";
import { Controller, FormProvider, useForm } from "react-hook-form";
import {
  ActionFunction,
  LoaderFunction,
  redirect,
  useFetcher,
  useLoaderData,
  useRouteLoaderData,
} from "react-router-dom";
import { COLORS } from "style";
import {
  Hospital,
  Mission,
  MissionAssessmentConditionEnum,
  MissionMedicalTeamEnum,
  MissionPlannedDepartureEnum,
  MissionStatusEnum,
  MissionTaskingTypeEnum,
  User,
} from "type/model/api";
import {
  CreateSecondaryMissionFormProps,
  createSecondaryMissionSchema,
  CreateSecondaryMissionTaskSchemaProps,
} from "type/schema";
import { checkRoleLoader, pathWithSearchParams } from "utility/router";
import { SinglePatientDetails } from "./SinglePatientDetails";
import { MultiPatientDetails } from "./MultiPatientDetails";
import { PatientHandoverType } from "type/model/enums";
import { UserRoles } from "type/model/view";
import { useEffect } from "react";
import { FormSelect } from "component/FormSelect";
import { Dashboard3Icon } from "component/Icons";
import { getApiErrorMessage } from "utility/error-message";

const getPatientApiPayload = (
  isMultiPatient: boolean,
  values: Partial<CreateSecondaryMissionFormProps>
): Partial<Mission> => {
  const result: Partial<Mission> = {
    status: MissionStatusEnum.Draft,
    aviation_risk: values.riskScore,
    tasking_type: MissionTaskingTypeEnum.Secondary,
    is_multipatient: isMultiPatient,
    planned_departure: values.plannedDeparture,
    planned_departure_at:
      values.departureTime && values.plannedDeparture !== "now"
        ? values.departureTime.toISOString()
        : values.plannedDeparture,
    tasks_attributes: values.tasks!.map((task, index) => ({
      position: (index + 1) * 100,
      task: "patient_transport",
      from_hospital_id: task.referringHospital,
      referring_handover_type: isMultiPatient ? task.referringHandoverType : null,
      to_hospital_id: task.destinationHospital,
      destination_handover_type: isMultiPatient ? task.destinationHandoverType : null,
      patient_identifier: isMultiPatient ? task.patientIdentifier : null,
      patient_priority: task.priority,
      requires_doctor: task.medicalTeam === MissionMedicalTeamEnum.DoctorRequired,
      needs_stretcher: isMultiPatient ? task.needsStretcher === "true" : null,
      can_travel_with_others: isMultiPatient ? task.canTravelWithOthers === "true" : null,
    })),
  };

  if (!isMultiPatient) {
    result.assessment_condition = values.specialCondition;
    result.is_drop_off_only = values.isDropOffOnly === "true";
  }

  return result;
};

export const createSecMissionLoader: LoaderFunction = async (params) => {
  await checkRoleLoader([UserRoles.Tasker])(params);

  let hospitals: Hospital[] = [];
  try {
    const hospitalsQuery = await api.queryClient.ensureQueryData({
      queryKey: ["hospitals"],
      queryFn: api.fetchHospitals,
    });
    hospitals = hospitalsQuery.data;
  } catch {}

  return { hospitals };
};

export const createSecMissionAction: ActionFunction = async ({ request }) => {
  const formValues = await request.formData();
  const missionValues = formValues.get("mission");
  if (missionValues && typeof missionValues === "string") {
    try {
      const response = await api.createMission({ mission: JSON.parse(missionValues) });
      const missionId = response.data.data.id;
      return redirect(pathWithSearchParams(`/main/dashboard/mission/${missionId}/recommendations`));
    } catch (error) {
      return { formError: getApiErrorMessage(error) };
    }
  } else {
    return { formError: "Invalid form data format!" };
  }
};

export const CreateSecondaryMission = () => {
  const { hospitals } = useLoaderData() as { hospitals: Hospital[] };
  const { currentUser } = useRouteLoaderData("main") as { currentUser: User };
  const currentUserTimeZone = currentUser.time_zone || DEFAULT_TIME_ZONE;
  const currentUserTimeZoneAbbreviation = format(Date.now(), "zzz", {
    timeZone: currentUserTimeZone,
    locale: enAU,
  });

  const fetcher = useFetcher();

  const defaultTaskValues: CreateSecondaryMissionTaskSchemaProps = {
    patientIdentifier: null,
    priority: null,
    referringHospital: hospitals[0]?.id ?? null,
    destinationHospital: hospitals[0]?.id ?? null,
    destinationHandoverType: PatientHandoverType.Hospital,
    canTravelWithOthers: "true",
    referringHandoverType: PatientHandoverType.Hospital,
    medicalTeam: MissionMedicalTeamEnum.FlightNurseOnly,
    needsStretcher: "false",
  };

  const formProps = useForm<Partial<CreateSecondaryMissionFormProps>>({
    resolver: zodResolver(createSecondaryMissionSchema),
    defaultValues: {
      multiPatient: "false",
      isDropOffOnly: "false",
      plannedDeparture: MissionPlannedDepartureEnum.Now,
      specialCondition: MissionAssessmentConditionEnum.TotalMedicalTime,
      tasks: [defaultTaskValues],
    },
    mode: "all",
  });

  const { handleSubmit, control, watch, resetField } = formProps;

  const plannedDepartureValue = watch("plannedDeparture");
  const isMultiPatient = watch("multiPatient") === "true";

  useEffect(() => {
    resetField("tasks");
  }, [isMultiPatient, resetField]);

  const onSubmit = async (values: Partial<CreateSecondaryMissionFormProps>) => {
    const formData = new FormData();
    const transformedData = getPatientApiPayload(isMultiPatient, values);
    formData.append("mission", JSON.stringify(transformedData));
    fetcher.submit(formData, {
      action: "/main/dashboard/mission/create/secondary",
      method: "post",
    });
  };

  return (
    <Stack component="form" onSubmit={handleSubmit(onSubmit)} noValidate sx={{ gap: 2 }}>
      <Stack
        sx={{
          flexGrow: 1,
          p: 4,
          border: `2px solid ${COLORS.WHITE_SMOKE}`,
          width: "100%",
          borderRadius: "20px",
          borderTop: 0,
          borderStartStartRadius: 0,
          borderStartEndRadius: 0,
        }}
      >
        <Stack sx={{ width: "100%", maxWidth: 720, marginLeft: "10%", gap: 4, alignItems: "flex-start" }}>
          <Stack sx={{ gap: 1 }}>
            <Typography variant="h2">Patient</Typography>
            <Controller
              name="multiPatient"
              control={control}
              render={({ field: { onChange, value, onBlur }, fieldState: { error } }) => (
                <FormRadioGroup
                  fullWidth
                  row
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  error={error?.message}
                  items={[
                    { label: "Single patient", value: "false" },
                    { label: "Multiple patient", value: "true" },
                  ]}
                />
              )}
            />
          </Stack>

          <FormProvider {...formProps}>
            {isMultiPatient ? (
              <MultiPatientDetails defaultTaskValues={defaultTaskValues} hospitals={hospitals} />
            ) : (
              <SinglePatientDetails hospitals={hospitals} />
            )}
          </FormProvider>

          <Stack sx={{ gap: 1 }}>
            <Typography variant="h2">Logistics</Typography>
            <Typography>Select the relevant mission times</Typography>
          </Stack>
          <Controller
            name="riskScore"
            control={control}
            render={({ field: { onChange, value, onBlur }, fieldState: { error } }) => (
              <FormSelect
                label="Risk score"
                placeholder="Select a risk score"
                displayEmpty={true}
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                error={error?.message}
                sx={{ minWidth: 350 }}
                startAdornment={
                  <InputAdornment position="start">
                    <Dashboard3Icon />
                  </InputAdornment>
                }
                items={RISK_SCORE_SELECT_ITEMS}
              />
            )}
          />
          <Controller
            name="plannedDeparture"
            control={control}
            render={({ field: { onChange, value, onBlur }, fieldState: { error } }) => (
              <FormRadioGroup
                fullWidth
                row
                label="Planned departure"
                required
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                error={error?.message}
                items={PLANNED_DEPARTURE_OPTIONS}
              />
            )}
          />
          {plannedDepartureValue === MissionPlannedDepartureEnum.SpecificTime ? (
            <Controller
              name="departureTime"
              control={control}
              render={({ field: { onChange, value, onBlur }, fieldState: { error } }) => (
                <FormDateTimePicker
                  icon="clock"
                  label={`Departure time (${currentUserTimeZoneAbbreviation})`}
                  required
                  placeholder="Enter the departing time"
                  sx={{ width: 350 }}
                  onChange={onChange}
                  onBlur={onBlur}
                  value={value}
                  error={error?.message}
                />
              )}
            />
          ) : null}
          {!isMultiPatient ? (
            <Controller
              name="isDropOffOnly"
              control={control}
              render={({ field: { onChange, value, onBlur }, fieldState: { error } }) => (
                <FormRadioGroup
                  fullWidth
                  row
                  label="Drop off only"
                  required
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  error={error?.message}
                  items={[
                    { label: "Yes", value: "true" },
                    { label: "No", value: "false" },
                  ]}
                />
              )}
            />
          ) : null}
        </Stack>
      </Stack>

      <Stack direction="row" sx={{ alignItems: "flex-start", justifyContent: "flex-end", gap: 4 }}>
        {fetcher.data?.formError ? <ErrorAlert>{fetcher.data.formError}</ErrorAlert> : null}
        <LoadingButton
          type="submit"
          loading={fetcher.state === "loading" || fetcher.state === "submitting"}
          variant="contained"
          sx={{ whiteSpace: "nowrap" }}
        >
          Next
        </LoadingButton>
      </Stack>
    </Stack>
  );
};
